import _slicedToArray from "/usr/app/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import { parseJsonSafely } from "@/utils/GlobalJavascriptFunction";
import { useEffect } from "react";
var LOCAL_STORAGE_LAST_POSITION_KEY = "last_position";
var LOCAL_STORAGE_SEARCH_RES_KEY = "last_search_results";
var LOCAL_STORAGE_PATH_TYPE_KEY = "last_path_type";
var LOCAL_STORAGE_LAST_PATH_KEY = "last_path";
var PATH_TYPE_MAP = {
  'filters': ['/filters'],
  'autos': ['/autos'],
  'years': ['/years'],
  'car-countries': ['/car-countries'],
  'details': ['/cardetail', '/car-compare', '/car/']
};
var isAllowedPagePath = function isAllowedPagePath(allowed_page_path) {
  if (__IS_SSR__) return false;
  return !!window.prevPath && window.prevPath.includes(allowed_page_path);
};
var cleanLastPosition = function cleanLastPosition() {
  if (__IS_SSR__) return;
  sessionStorage.removeItem(LOCAL_STORAGE_LAST_POSITION_KEY);
};
var cleanLastStorageData = function cleanLastStorageData() {
  if (__IS_SSR__) return;
  sessionStorage.removeItem(LOCAL_STORAGE_SEARCH_RES_KEY);
};
var preserveData = function preserveData(searchResultsData) {
  if (__IS_SSR__) return false;
  sessionStorage.setItem(LOCAL_STORAGE_SEARCH_RES_KEY, JSON.stringify(searchResultsData));
};
var preserveScrollPosition = function preserveScrollPosition() {
  if (__IS_SSR__) return false;
  sessionStorage.setItem(LOCAL_STORAGE_LAST_POSITION_KEY, window.scrollY.toString());
};

// Strip the `page` query param so pagination within the SAME search is not
// treated as a path change. Infinite scroll rewrites the URL to ?...&page=N via
// history.replaceState WITHOUT remounting, so last_path stays frozen at the
// page-less URL; on back-navigation currentPath carries &page=N and would
// otherwise mismatch last_path, wrongly wiping the preserved posts/scroll.
var normalizePath = function normalizePath(fullPath) {
  var _fullPath$split = fullPath.split("?"),
    _fullPath$split2 = _slicedToArray(_fullPath$split, 2),
    pathname = _fullPath$split2[0],
    _fullPath$split2$ = _fullPath$split2[1],
    query = _fullPath$split2$ === void 0 ? "" : _fullPath$split2$;
  var params = new URLSearchParams(query);
  params["delete"]("page");
  var normalizedQuery = params.toString();
  return normalizedQuery ? "".concat(pathname, "?").concat(normalizedQuery) : pathname;
};
var getCurrentPathType = function getCurrentPathType(pathname) {
  for (var _i = 0, _Object$entries = Object.entries(PATH_TYPE_MAP); _i < _Object$entries.length; _i++) {
    var _Object$entries$_i = _slicedToArray(_Object$entries[_i], 2),
      type = _Object$entries$_i[0],
      patterns = _Object$entries$_i[1];
    if (patterns.some(function (pattern) {
      return pathname.includes(pattern);
    })) {
      return type;
    }
  }
  return null;
};
var usePreserveData = function usePreserveData() {
  var lastScrollPosition = 0;
  var lastSearchResultsData = null;
  var shouldFetchNewData = false;
  if (!__IS_SSR__) {
    // Get current pathname and path type
    var currentPath = window.location.pathname + window.location.search;
    var currentPathType = getCurrentPathType(currentPath);
    var lastStoredPath = sessionStorage.getItem(LOCAL_STORAGE_LAST_PATH_KEY);

    // Get previous path type from localStorage
    var previousPathType = sessionStorage.getItem(LOCAL_STORAGE_PATH_TYPE_KEY);

    // Check if we need to fetch new data based on path changes
    var pathTypeChanged = previousPathType && currentPathType && previousPathType !== currentPathType;
    // Compare page-less paths so scrolling to page N within the same search
    // does not count as a path change (see normalizePath).
    var storedPathChanged = lastStoredPath && normalizePath(currentPath) !== normalizePath(lastStoredPath);
    shouldFetchNewData = pathTypeChanged && !(['filters', 'autos', 'years', 'car-countries'].includes(previousPathType) && ['filters', 'autos', 'years', 'car-countries'].includes(currentPathType)) || storedPathChanged;
    if (shouldFetchNewData) {
      // Clear data when path changes
      cleanLastPosition();
      cleanLastStorageData();
    } else {
      // Only load session data if path hasn't changed
      lastScrollPosition = Number(sessionStorage.getItem(LOCAL_STORAGE_LAST_POSITION_KEY));
      lastSearchResultsData = parseJsonSafely(sessionStorage.getItem(LOCAL_STORAGE_SEARCH_RES_KEY));
    }

    // Update the stored path type and full path
    if (currentPathType) {
      sessionStorage.setItem(LOCAL_STORAGE_PATH_TYPE_KEY, currentPathType);
    }
    sessionStorage.setItem(LOCAL_STORAGE_LAST_PATH_KEY, currentPath);
  }

  // Don't automatically clean position to help prevent CLS
  // cleanLastPosition();

  return {
    preserveData: preserveData,
    preserveScrollPosition: preserveScrollPosition,
    lastScrollPosition: Number(lastScrollPosition),
    lastSearchResultsData: lastSearchResultsData,
    shouldFetchNewData: shouldFetchNewData
  };
};
export var useMaintainSearchResultsDataStorage = function useMaintainSearchResultsDataStorage() {
  var _window, _window2, _window3;
  if (__IS_SSR__) {
    return;
  }
  var pathname = (_window = window) === null || _window === void 0 ? void 0 : _window.location.pathname;
  var path = ((_window2 = window) === null || _window2 === void 0 ? void 0 : _window2.location.pathname) + ((_window3 = window) === null || _window3 === void 0 ? void 0 : _window3.location.search);
  useEffect(function () {
    // Check if current path is a supported type
    var currentPathType = getCurrentPathType(pathname);
    if (!currentPathType) {
      cleanLastPosition();
      cleanLastStorageData();
      // Also clear the path type
      sessionStorage.removeItem(LOCAL_STORAGE_PATH_TYPE_KEY);
    }
  }, [pathname]);
};
export default usePreserveData;